import { FC, useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { Users, Ship, Clock, FileWarning, UserCheck, Anchor } from 'lucide-react'
import { toast } from 'react-toastify'
import { showErrorToast } from '../../../../shared/utils/apiErrorMessage'
import DashboardModal from './DashboardModal'
import type { ActionButton } from './DashboardModal'
import type { CrewDerivedKpis } from './types'
import type { ExpiryRow } from './crewDashboardData'

const statusClass = (value: string) => {
  const status = value.toUpperCase()
  if (['APPROVED', 'REVIEWED', 'VALID'].includes(status)) return 'bg-emerald-50 text-emerald-700'
  if (['REJECTED', 'EXPIRED'].includes(status)) return 'bg-red-50 text-red-700'
  if (['PENDING', 'SUBMITTED', 'EXPIRING'].includes(status)) return 'bg-amber-50 text-amber-700'
  return 'bg-slate-100 text-slate-600'
}

export const MonthlyPreviewCard: FC<{title: string; month?: string; onMonthChange?: (month: string) => void; onViewAll: () => void; rows: any[] | null; columns: {label: string; field: string}[]; error?: boolean; note?: string}> = ({title, month, onMonthChange, onViewAll, rows, columns, error, note}) => (
  <section className='bg-white rounded-2xl border border-slate-200 shadow-sm p-4 md:p-5'>
    <div className='flex flex-wrap items-center justify-between gap-2 mb-3'>
      <h6 className='text-sm font-bold text-slate-900'>{title}</h6>
      <div className='flex items-center gap-3'>
        {onMonthChange && <input type='month' aria-label={`${title} month`} value={month} onChange={e => {if (/^\d{4}-(0[1-9]|1[0-2])$/.test(e.target.value)) onMonthChange(e.target.value)}} className='text-[10px] border border-slate-200 rounded px-2 py-1' />}
        <button onClick={onViewAll} className='text-[10px] font-semibold text-blue-600 whitespace-nowrap'>View all →</button>
      </div>
    </div>
    {note && <p className='text-[10px] text-slate-500 mb-2'>{note}</p>}
    <div className='overflow-x-auto max-h-[250px] overflow-y-auto'><table className='w-full text-[11px]'>
      <thead><tr className='text-[10px] text-slate-400 uppercase border-b'>{columns.map(c => <th key={c.field} className='text-left p-2 font-semibold'>{c.label}</th>)}</tr></thead>
      <tbody>{error || rows === null || rows.length === 0 ? <tr><td colSpan={columns.length} className='p-6 text-center text-slate-400'>{error ? 'Data unavailable' : rows === null ? 'Loading…' : month ? 'No records for this month' : 'No records found'}</td></tr> : rows.slice(0, 5).map((row, i) => <tr key={i} className='even:bg-blue-50/60 border-b border-slate-100'>{columns.map(c => <td key={c.field} className='p-2 text-slate-700'>{['status', 'workflowStatus'].includes(c.field) ? <span className={`rounded px-2 py-1 text-[10px] ${statusClass(String(row[c.field] || ''))}`}>{row[c.field] || '—'}</span> : String(row[c.field] ?? '—')}</td>)}</tr>)}</tbody>
    </table></div>
  </section>
)

export const CrewRecordsModal: FC<{open: boolean; onClose: () => void; title: string; rows: any[]; columns: {label: string; field: string}[]; route?: string; routeLabel?: string; month?: string; onMonthChange?: (month: string) => void; loading?: boolean; error?: boolean}> = ({open, onClose, title, rows, columns, route, routeLabel, month, onMonthChange, loading, error}) => {
  const [query, setQuery] = useState('')
  useEffect(() => { if (open) setQuery('') }, [open, title])
  const navigate = useNavigate()
  const filtered = rows.filter(row => columns.some(column => String(row[column.field] ?? '').toLowerCase().includes(query.toLowerCase())))
  const actions: ActionButton[] = loading || error ? [] : [{label: 'Download CSV', onClick: () => downloadCsv('crew-dashboard.csv', columns.map(c => c.label), filtered.map(row => columns.map(c => String(row[c.field] ?? ''))))}]
  if (route) actions.push({label: routeLabel || 'View page', variant: 'primary', onClick: () => navigate(route)})
  return <DashboardModal open={open} onClose={onClose} title={title} wide searchQuery={query} onSearchChange={setQuery} actions={actions}>
    {onMonthChange && <label className='block mb-4 text-xs text-slate-600'>Month <input aria-label='Month' type='month' value={month || ''} onChange={e => { if (/^\d{4}-(0[1-9]|1[0-2])$/.test(e.target.value)) onMonthChange(e.target.value) }} className='ml-2 rounded-lg border border-slate-200 px-3 py-2' /></label>}
    {loading || error ? <p className='text-center py-6 text-xs text-slate-500'>{error ? (onMonthChange ? 'Data unavailable for this month. Check access or try another month.' : 'Data unavailable. Check access or refresh.') : 'Loading selected month…'}</p> : <div className='overflow-x-auto'><table className='w-full text-[11px]'><thead><tr className='text-slate-400 text-[10px] uppercase border-b'>{columns.map(c => <th className='text-left p-2' key={c.field}>{c.label}</th>)}</tr></thead><tbody>
      {filtered.map((row, index) => <tr className='border-b border-slate-100 even:bg-blue-50/60' key={index}>{columns.map(c => <td className='p-2 text-slate-700' key={c.field}>{['approvalStatus', 'workflowStatus', 'status'].includes(c.field) && row[c.field] ? <span className={`inline-block rounded px-2 py-1 text-[10px] font-semibold ${statusClass(String(row[c.field]))}`}>{String(row[c.field])}</span> : String(row[c.field] ?? '—')}</td>)}</tr>)}
      {filtered.length === 0 && <tr><td className='text-center p-6' colSpan={columns.length}>No matching records</td></tr>}
    </tbody></table></div>}
  </DashboardModal>
}

export const ExpiryRecordsModal: FC<{open: boolean; onClose: () => void; rows: ExpiryRow[] | null; error: boolean}> = ({open, onClose, rows, error}) => {
  if (!rows) return <DashboardModal open={open} onClose={onClose} title='Expiring Documents'><p>{error ? 'Document expiry data is unavailable. Check access and refresh.' : 'Loading document expiry data…'}</p></DashboardModal>
  const now = new Date()
  const today = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-${String(now.getDate()).padStart(2, '0')}`
  return <CrewRecordsModal open={open} onClose={onClose} title='Expired or expiring within 30 days' route='/crewingtest/crewinglist' routeLabel='View Crew Master' rows={rows.map(row => ({...row, status: row.expiry < today ? 'Expired' : 'Expiring'}))} columns={[{label: 'Crew', field: 'crewName'}, {label: 'Document', field: 'document'}, {label: 'Expiry date', field: 'expiry'}, {label: 'Status', field: 'status'}]} />
}

const fmtDate = (d: string | null | undefined) => {
  if (!d) return '—'
  return new Date(d).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' })
}

const StatusBadge: FC<{ status: string }> = ({ status }) => {
  const s = (status || '').toUpperCase()
  const isOn = s === 'SIGNED_ON'
  return (
    <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded text-[10px] font-medium ${
      isOn ? 'bg-emerald-50 text-emerald-600' : 'bg-red-50 text-red-600'
    }`}>
      <span className={`w-1.5 h-1.5 rounded-full ${isOn ? 'bg-emerald-500' : 'bg-red-500'}`} />
      {isOn ? 'Onboard' : 'Signed Off'}
    </span>
  )
}

/* ─── Helpers ─── */
function downloadCsv(filename: string, headers: string[], rows: string[][]) {
  try {
    const csv = [headers.join(','), ...rows.map((r) => r.map((c) => `"${(c || '').replace(/"/g, '""')}"`).join(','))].join('\n')
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' })
    const link = document.createElement('a')
    link.href = URL.createObjectURL(blob)
    link.download = filename
    link.click()
    URL.revokeObjectURL(link.href)
    toast.success(`${filename} exported successfully.`)
  } catch (err) {
    console.error(`Failed to export ${filename}:`, err)
    showErrorToast(err, `Failed to export ${filename}.`)
  }
}

/* ─── KpiDetailModal ─── */
interface KpiDetailProps {
  open: boolean
  onClose: () => void
  kpis: CrewDerivedKpis
}

export const KpiDetailModal: FC<KpiDetailProps> = ({ open, onClose, kpis }) => {
  const items = [
    { label: 'Total Crew', value: kpis.totalCrew, icon: Users, color: 'text-blue-600', bg: 'bg-blue-50' },
    { label: 'Onboard Now', value: kpis.onboardNow, icon: Ship, color: 'text-emerald-600', bg: 'bg-emerald-50' },
    { label: 'Pending Approvals', value: kpis.pendingApprovals, icon: Clock, color: 'text-amber-600', bg: 'bg-amber-50' },
    { label: 'Certifications (Expiring)', value: kpis.expiringCerts, icon: FileWarning, color: 'text-red-600', bg: 'bg-red-50' },
    { label: 'Signed Off', value: kpis.signedOff, icon: UserCheck, color: 'text-purple-600', bg: 'bg-purple-50' },
    { label: 'Vessels', value: kpis.vessels, icon: Anchor, color: 'text-teal-600', bg: 'bg-teal-50' },
  ]

  const actions: ActionButton[] = [
    {
      label: 'Download Summary',
      onClick: () => downloadCsv('crew-kpi-summary.csv',
        ['Metric', 'Value'],
        items.map((i) => [i.label, String(i.value)]),
      ),
    },
  ]

  return (
    <DashboardModal open={open} onClose={onClose} title='KPI Details' actions={actions}>
      <div className='grid grid-cols-2 md:grid-cols-3 gap-4'>
        {items.map((item, i) => (
          <div key={i} className={`flex flex-col items-center justify-center p-4 rounded-xl ${item.bg}`}>
            <item.icon className={`w-6 h-6 ${item.color} mb-2`} />
            <div className={`text-2xl font-bold ${item.color}`}>{item.value}</div>
            <div className='text-[10px] text-slate-500 mt-1 text-center'>{item.label}</div>
          </div>
        ))}
      </div>
    </DashboardModal>
  )
}

/* ─── SignOnOffModal (Full Table) ─── */
interface SignOnOffModalProps {
  open: boolean
  onClose: () => void
  records: any[]
  title?: string
}

export const SignOnOffModal: FC<SignOnOffModalProps> = ({ open, onClose, records, title = 'All Sign-On / Off Records' }) => {
  const navigate = useNavigate()
  const [searchQuery, setSearchQuery] = useState('')
  useEffect(() => { if (open) setSearchQuery('') }, [open, title])
  const q = searchQuery.toLowerCase()
  const filtered = records.filter((r: any) =>
    (r.crewName || '').toLowerCase().includes(q) ||
    (r.vesselName || '').toLowerCase().includes(q) ||
    (r.portSignOn || '').toLowerCase().includes(q)
  )

  const actions: ActionButton[] = [
    {
      label: 'Download CSV',
      onClick: () => downloadCsv('sign-on-off-records.csv',
        ['Crew Name', 'Vessel', 'Sign-On Date', 'Sign-Off Date', 'Port', 'Status'],
        filtered.map((r: any) => [r.crewName || '', r.vesselName || '', fmtDate(r.signOnDate), fmtDate(r.signOffDate), r.portSignOn || '', r.status || '']),
      ),
    },
    { label: 'View Assignments', variant: 'primary', onClick: () => navigate('/crewing/assignment') },
  ]

  return (
    <DashboardModal
      open={open} onClose={onClose} title={title} wide
      actions={actions}
      searchQuery={searchQuery} onSearchChange={setSearchQuery} searchPlaceholder='Search by crew, vessel or port...'
    >
      <div className='overflow-x-auto'>
        <table className='w-full text-[11px]'>
          <thead>
            <tr className='text-slate-400 text-[10px] uppercase tracking-wider border-b border-slate-200'>
              <th className='text-left font-semibold py-2 pr-2'>Crew Name</th>
              <th className='text-left font-semibold py-2 px-2'>Vessel</th>
              <th className='text-left font-semibold py-2 px-2'>Sign-On Date</th>
              <th className='text-left font-semibold py-2 px-2'>Sign-Off Date</th>
              <th className='text-left font-semibold py-2 px-2'>Port</th>
              <th className='text-right font-semibold py-2 pl-2'>Status</th>
            </tr>
          </thead>
          <tbody className='divide-y divide-slate-100'>
            {filtered.length === 0 ? (
              <tr>
                <td colSpan={6} className='text-center text-slate-400 py-8 text-xs'>No sign-on/off records found</td>
              </tr>
            ) : (
              filtered.map((rec: any, i: number) => (
                <tr key={i} className='even:bg-blue-50/60 hover:bg-slate-50 transition-colors'>
                  <td className='py-2.5 pr-2 font-semibold text-slate-800'>{rec.crewName || '—'}</td>
                  <td className='py-2.5 px-2 text-slate-500'>{rec.vesselName || '—'}</td>
                  <td className='py-2.5 px-2 text-slate-700'>{fmtDate(rec.signOnDate)}</td>
                  <td className='py-2.5 px-2 text-slate-700'>{fmtDate(rec.signOffDate)}</td>
                  <td className='py-2.5 px-2 text-slate-500'>{rec.portSignOn || '—'}</td>
                  <td className='py-2.5 pl-2 text-right'><StatusBadge status={rec.status} /></td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
    </DashboardModal>
  )
}

/* ─── PendingActionsModal ─── */
interface PendingAction {
  label: string
  desc: string
  color: 'warning' | 'danger' | 'success'
}

interface PendingActionsModalProps {
  open: boolean
  onClose: () => void
  items: PendingAction[]
}

export const PendingActionsModal: FC<PendingActionsModalProps> = ({ open, onClose, items }) => {
  const navigate = useNavigate()
  const dotMap: Record<string, string> = { warning: 'bg-amber-500', danger: 'bg-red-500', success: 'bg-emerald-500' }

  const actions: ActionButton[] = items.some((a) => a.color !== 'success')
    ? [{ label: 'View All', onClick: () => navigate('/crewing/overview') }]
    : []

  return (
    <DashboardModal open={open} onClose={onClose} title='Pending Actions' actions={actions.length > 0 ? actions : undefined}>
      <div className='space-y-3'>
        {items.length === 0 ? (
          <div className='text-center text-slate-400 py-8 text-xs'>No pending actions</div>
        ) : (
          items.map((action, i) => (
            <div key={i} className='flex items-center gap-3 p-3 rounded-xl border border-slate-200 bg-slate-50'>
              <span className={`w-2 h-2 rounded-full flex-shrink-0 ${dotMap[action.color]}`} />
              <div className='flex-1'>
                <div className='text-[11px] font-semibold text-slate-800'>{action.label}</div>
                <div className='text-[10px] text-slate-500'>{action.desc}</div>
              </div>
            </div>
          ))
        )}
      </div>
    </DashboardModal>
  )
}

/* ─── CertificationModal ─── */
interface CertificationModalProps {
  open: boolean
  onClose: () => void
  crewList: any[]
}

export const CertificationModal: FC<CertificationModalProps> = ({ open, onClose, crewList }) => {
  const navigate = useNavigate()
  const [searchQuery, setSearchQuery] = useState('')
  const q = searchQuery.toLowerCase()
  const expiringCrew = crewList.filter((c: any) => {
    const docs = [...(c.certifications || []), ...(c.documents || [])]
    return docs.some((d: any) => d.documentName && d.documentName.toLowerCase().includes('expir'))
  }).filter((c: any) => (c.name || '').toLowerCase().includes(q))

  const actions: ActionButton[] = [
    {
      label: 'Download CSV',
      onClick: () => downloadCsv('expiring-certifications.csv',
        ['Crew Name', 'Rank', 'Department', 'Status'],
        expiringCrew.map((c: any) => [c.name || '', `Rank #${c.rankId}`, c.department || '', 'Needs Attention']),
      ),
    },
    {
      label: 'Send Reminder',
      variant: 'danger',
      onClick: () => toast.info('Reminder sent to crew members with expiring certifications.'),
    },
  ]

  return (
    <DashboardModal
      open={open} onClose={onClose} title='Expiring Certifications' wide
      actions={actions}
      searchQuery={searchQuery} onSearchChange={setSearchQuery} searchPlaceholder='Search by crew name...'
    >
      <div className='overflow-x-auto'>
        <table className='w-full text-[11px]'>
          <thead>
            <tr className='text-slate-400 text-[10px] uppercase tracking-wider border-b border-slate-200'>
              <th className='text-left font-semibold py-2 pr-2'>Crew Name</th>
              <th className='text-left font-semibold py-2 px-2'>Rank</th>
              <th className='text-left font-semibold py-2 px-2'>Department</th>
              <th className='text-right font-semibold py-2 pl-2'>Status</th>
            </tr>
          </thead>
          <tbody className='divide-y divide-slate-100'>
            {expiringCrew.length === 0 ? (
              <tr>
                <td colSpan={4} className='text-center text-slate-400 py-8 text-xs'>No expiring certifications found</td>
              </tr>
            ) : (
              expiringCrew.map((crew: any, i: number) => (
                <tr key={i} className='hover:bg-slate-50 transition-colors'>
                  <td className='py-2.5 pr-2 font-semibold text-slate-800'>{crew.name || '—'}</td>
                  <td className='py-2.5 px-2 text-slate-500'>Rank #{crew.rankId}</td>
                  <td className='py-2.5 px-2 text-slate-500'>{crew.department || '—'}</td>
                  <td className='py-2.5 pl-2 text-right'>
                    <span className='inline-flex items-center gap-1 px-2 py-0.5 rounded text-[10px] font-medium bg-red-50 text-red-600'>
                      Needs Attention
                    </span>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
    </DashboardModal>
  )
}

/* ─── TrainingModal ─── */
interface TrainingModalProps {
  open: boolean
  onClose: () => void
  trainings: any[]
  title?: string
}

export const TrainingModal: FC<TrainingModalProps> = ({ open, onClose, trainings, title = 'Training Pipeline' }) => {
  const navigate = useNavigate()
  const [searchQuery, setSearchQuery] = useState('')
  const q = searchQuery.toLowerCase()
  const filtered = trainings.filter((t: any) =>
    (t.crewName || '').toLowerCase().includes(q) ||
    (t.topic || '').toLowerCase().includes(q) ||
    (t.trainingType || '').toLowerCase().includes(q)
  )

  const actions: ActionButton[] = [
    {
      label: 'Download CSV',
      onClick: () => downloadCsv('training-pipeline.csv',
        ['Crew Name', 'Topic', 'Type', 'Mode', 'Status'],
        filtered.map((t: any) => [t.crewName || '', t.topic || '', t.trainingType || '', t.mode || '', t.workflowStatus || 'DRAFT']),
      ),
    },
    {
      label: 'View Training',
      onClick: () => navigate('/crewing/crewtraining'),
    },
  ]

  return (
    <DashboardModal
      open={open} onClose={onClose} title={title} wide
      actions={actions}
      searchQuery={searchQuery} onSearchChange={setSearchQuery} searchPlaceholder='Search by crew, topic or type...'
    >
      <div className='overflow-x-auto'>
        <table className='w-full text-[11px]'>
          <thead>
            <tr className='text-slate-400 text-[10px] uppercase tracking-wider border-b border-slate-200'>
              <th className='text-left font-semibold py-2 pr-2'>Crew Name</th>
              <th className='text-left font-semibold py-2 px-2'>Topic</th>
              <th className='text-left font-semibold py-2 px-2'>Type</th>
              <th className='text-left font-semibold py-2 px-2'>Mode</th>
              <th className='text-right font-semibold py-2 pl-2'>Status</th>
            </tr>
          </thead>
          <tbody className='divide-y divide-slate-100'>
            {filtered.length === 0 ? (
              <tr>
                <td colSpan={5} className='text-center text-slate-400 py-8 text-xs'>No training records found</td>
              </tr>
            ) : (
              filtered.map((t: any, i: number) => (
                <tr key={i} className='hover:bg-slate-50 transition-colors'>
                  <td className='py-2.5 pr-2 font-semibold text-slate-800'>{t.crewName || '—'}</td>
                  <td className='py-2.5 px-2 text-slate-700'>{t.topic || '—'}</td>
                  <td className='py-2.5 px-2 text-slate-500'>{t.trainingType || '—'}</td>
                  <td className='py-2.5 px-2 text-slate-500'>{t.mode || '—'}</td>
                  <td className='py-2.5 pl-2 text-right'>
                    <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded text-[10px] font-medium ${
                      t.workflowStatus === 'REVIEWED' ? 'bg-emerald-50 text-emerald-600' :
                      t.workflowStatus === 'REJECTED' ? 'bg-red-50 text-red-600' :
                      'bg-amber-50 text-amber-600'
                    }`}>
                      {t.workflowStatus || 'DRAFT'}
                    </span>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
    </DashboardModal>
  )
}

/* ─── RestHourModal ─── */
interface RestHourModalProps {
  open: boolean
  onClose: () => void
}

export const RestHourModal: FC<RestHourModalProps> = ({ open, onClose }) => (
  <DashboardModal open={open} onClose={onClose} title='Rest Hour Compliance'>
    <div className='text-center text-slate-400 py-8 text-xs'>
      Rest hour compliance data will appear here when a dedicated API endpoint is available.
    </div>
  </DashboardModal>
)

/* ─── CrewChangeModal ─── */
interface CrewChangeModalProps {
  open: boolean
  onClose: () => void
}

export const CrewChangeModal: FC<CrewChangeModalProps> = ({ open, onClose }) => (
  <DashboardModal open={open} onClose={onClose} title='Upcoming Crew Changes'>
    <div className='text-center text-slate-400 py-8 text-xs'>
      Upcoming crew change timeline will appear here when a dedicated API endpoint is available.
    </div>
  </DashboardModal>
)

/* ─── OpenPositionsModal ─── */
interface OpenPositionsModalProps {
  open: boolean
  onClose: () => void
}

export const OpenPositionsModal: FC<OpenPositionsModalProps> = ({ open, onClose }) => (
  <DashboardModal open={open} onClose={onClose} title='Open Positions'>
    <div className='text-center text-slate-400 py-8 text-xs'>
      Open / vacant positions will appear here when a dedicated API endpoint is available.
    </div>
  </DashboardModal>
)
